// Pure mappers between the wizard's local state and the backend's
// /api/resume section `content` dicts (see ResumeContentValidation in backend).

export const emptyExperience = () => ({
  title: '', company: '', location: '', start: '', end: '', current: false, description: '',
});

export const emptyEducation = () => ({ degree: '', institution: '', year: '', score: '' });

export const emptyProject = () => ({ name: '', description: '', link: '' });

export const emptyCertification = () => ({ name: '', issuer: '', year: '' });

export function emptyWizardState() {
  return {
    profile: { name: '', email: '', phone: '', location: '', summary: '' },
    experiences: [],
    educations: [],
    skills: [],
    projects: [],
    certifications: [],
    languages: [],
    achievements: [],
  };
}

const isBlankObject = (obj) =>
  Object.values(obj).every((v) => v === '' || v === false || v == null);

const filled = (rows) => (rows || []).filter((r) => !isBlankObject(r));

export function sectionIsValid(sectionType, state) {
  switch (sectionType) {
    case 'profile': {
      const { name, email, phone } = state.profile;
      return Boolean(name.trim() && email.trim() && phone.trim());
    }
    case 'experience':
      return filled(state.experiences).every((e) => e.title.trim() && e.company.trim());
    case 'education':
      return filled(state.educations).every((e) => e.degree.trim() && e.institution.trim());
    default:
      return true;
  }
}

export function buildSectionContent(sectionType, state) {
  switch (sectionType) {
    case 'profile':
      return { ...state.profile };
    case 'experience':
      return { experiences: filled(state.experiences) };
    case 'education':
      return { educations: filled(state.educations) };
    case 'skills':
      return { skills: state.skills };
    case 'projects':
      return { projects: filled(state.projects) };
    case 'certifications':
      return { certifications: filled(state.certifications) };
    case 'languages':
      return { languages: state.languages };
    case 'achievements':
      return {
        achievements: (state.achievements || [])
          .map((a) => (typeof a === 'string' ? a : `${a.title || a.name || ''} — ${a.description || ''}`).trim())
          .filter((a) => a && a !== '—'),
      };
    default:
      return {};
  }
}

// Sections that actually have data, in backend section_type terms.
export function nonEmptySections(state) {
  const out = [];
  if (state.profile.name || state.profile.email || state.profile.phone) out.push('profile');
  if (filled(state.experiences).length) out.push('experience');
  if (filled(state.educations).length) out.push('education');
  if (state.skills.length) out.push('skills');
  if (filled(state.projects).length) out.push('projects');
  if (filled(state.certifications).length) out.push('certifications');
  if (state.languages.length) out.push('languages');
  if ((state.achievements || []).filter((a) => a && (typeof a === 'string' ? a.trim() : a.title)).length) out.push('achievements');
  return out;
}

const mergeRow = (empty, row) => ({ ...empty, ...Object.fromEntries(
  Object.entries(row || {}).filter(([k, v]) => k in empty && v != null)
) });

// Prefill wizard state from /upload-resume `parsed_sections` / `parsed_data`.
export function fromParsedSections(parsed) {
  const state = emptyWizardState();
  if (!parsed || typeof parsed !== 'object') return state;

  const profile = parsed.profile || {};
  state.profile = mergeRow(state.profile, profile);

  const getList = (val) => {
    if (Array.isArray(val)) return val;
    if (val && typeof val === 'object') return val.experiences || val.educations || val.projects || val.certifications || val.items || [];
    return [];
  };

  state.experiences = getList(parsed.experience).map((e) => ({
    title: e.title || '',
    company: e.company || '',
    location: e.location || '',
    start: e.start_date || e.start || '',
    end: e.end_date || e.end || '',
    current: String(e.end_date || e.end || '').toLowerCase().includes('present'),
    description: Array.isArray(e.responsibilities) ? e.responsibilities.join(' ') : (e.description || ''),
  }));

  state.educations = getList(parsed.education).map((e) => ({
    degree: e.degree || '',
    institution: e.institution || '',
    year: e.graduation_year || e.year || '',
    score: e.gpa || e.score || '',
  }));

  state.projects = getList(parsed.projects).map((p) => ({
    name: p.name || '',
    description: p.description || '',
    link: p.url || p.link || '',
  }));

  state.certifications = getList(parsed.certifications).map((c) => ({
    name: c.name || c.title || '',
    issuer: c.issuer || '',
    year: c.year || c.date || '',
  }));

  const parseList = (v, preferredKey) => {
    if (!v) return [];
    if (Array.isArray(v)) {
      return v.map((x) => {
        if (typeof x === 'string') return x.trim();
        if (x && typeof x === 'object') {
          const parts = [x.title || x.name || x.skill, x.description].filter(Boolean);
          return parts.join(' — ');
        }
        return '';
      }).filter(Boolean);
    }
    if (typeof v === 'object') {
      if (preferredKey && Array.isArray(v[preferredKey])) {
        return parseList(v[preferredKey]);
      }
      // Categorized dictionary (e.g. { "Languages": ["Python", "JS"], "Databases": ["PostgreSQL"] })
      return Object.values(v)
        .flat()
        .map((x) => (typeof x === 'string' ? x.trim() : (x?.name || x?.skill || '')))
        .filter(Boolean);
    }
    return [];
  };

  state.skills = parseList(parsed.skills, 'skills');
  state.languages = parseList(parsed.languages, 'languages');
  state.achievements = parseList(parsed.achievements, 'achievements');

  return state;
}
